import {
	AlertOctagon,
	GitPullRequest,
	GitPullRequestClosed,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ITEMS_PER_PAGE } from "../../constants";
import { needsReviewerFollowUp } from "../../hooks/reviewerThreads";
import { sortStackedMergeRequests } from "../../stackedMergeRequests";
import { CollapsibleSection } from "../CollapsibleSection";
import { CountChip } from "../CountChip";
import { MergeRequest } from "../MergeRequest";
import { PaginationButtons } from "../PaginationButtons";
import { RecentPushes } from "../RecentPushes";

const REVIEW_SCORES = {
	UNREVIEWED: 1,
	UNAPPROVED: 1,
	REVIEW_STARTED: 0,
	REVIEWED: -1,
	REQUESTED_CHANGES: -1,
	APPROVED: -1,
};

export const StandardView = ({
	pendingReviewMRs,
	myOpenMRs,
	recentlyClosedMRs,
	loading,
	loadingClosed,
	error,
	pushesWithoutMR,
	sortOption,
	showPendingWithProjectRules,
	showPendingWithAllApprovals,
	greyOutBlockingComments,
	enableCommentsExpansion = false,
}) => {
	const [pendingReviewPage, setPendingReviewPage] = useState(1);
	const [myOpenPage, setMyOpenPage] = useState(1);
	const [recentlyClosedPage, setRecentlyClosedPage] = useState(1);
	const previousSortOption = useRef(sortOption);

	useEffect(() => {
		// Avoid resetting pagination on initial mount; only reset after sort changes.
		if (previousSortOption.current === sortOption) return;
		previousSortOption.current = sortOption;
		setPendingReviewPage(1);
		setMyOpenPage(1);
		setRecentlyClosedPage(1);
	}, [sortOption]);

	const sortedPendingReviewMRs = useMemo(
		() =>
			sortStackedMergeRequests(pendingReviewMRs, (a, b) => {
				if (a.draft !== b.draft) return a.draft ? 1 : -1;

				const aHasFailedMergeTrain =
					a.headPipeline?.mergeRequestEventType === "MERGE_TRAIN" &&
					a.headPipeline?.failedJobs > 0;
				const bHasFailedMergeTrain =
					b.headPipeline?.mergeRequestEventType === "MERGE_TRAIN" &&
					b.headPipeline?.failedJobs > 0;
				if (aHasFailedMergeTrain !== bHasFailedMergeTrain)
					return aHasFailedMergeTrain ? -1 : 1;

				// A reply on the viewer's thread makes the MR theirs to act on again,
				// so rank it with unreviewed work instead of what they already handled.
				const aFollowUp = needsReviewerFollowUp(a);
				const bFollowUp = needsReviewerFollowUp(b);
				const aReview = aFollowUp
					? REVIEW_SCORES.UNREVIEWED
					: REVIEW_SCORES[a.myReview];
				const bReview = bFollowUp
					? REVIEW_SCORES.UNREVIEWED
					: REVIEW_SCORES[b.myReview];
				if (aReview !== bReview) {
					return bReview - aReview;
				}

				if (greyOutBlockingComments) {
					const aHasBlockingHumanDiscussion =
						a.discussions > 0 && a.hasHumanReview && !aFollowUp;
					const bHasBlockingHumanDiscussion =
						b.discussions > 0 && b.hasHumanReview && !bFollowUp;
					if (aHasBlockingHumanDiscussion !== bHasBlockingHumanDiscussion) {
						return aHasBlockingHumanDiscussion ? 1 : -1;
					}
				}

				if ((a.author?.bot ?? false) !== (b.author?.bot ?? false)) {
					return (a.author?.bot ?? false) ? 1 : -1;
				}

				if (sortOption === "createdAt") {
					return new Date(b.createdAt) - new Date(a.createdAt);
				}
				return new Date(b.updatedAt) - new Date(a.updatedAt);
			}),
		[pendingReviewMRs, greyOutBlockingComments, sortOption],
	);

	const sortedMyOpenMRs = useMemo(
		() =>
			sortStackedMergeRequests(myOpenMRs, (a, b) => {
				if (sortOption === "createdAt") {
					return new Date(b.createdAt) - new Date(a.createdAt);
				}
				return new Date(b.updatedAt) - new Date(a.updatedAt);
			}),
		[myOpenMRs, sortOption],
	);

	const sortedRecentlyClosedMRs = [...recentlyClosedMRs].sort((a, b) => {
		return (
			new Date(b.closedAt || b.updatedAt) - new Date(a.closedAt || a.updatedAt)
		);
	});

	const filterPendingMRs = (mrs) => {
		return mrs
			.filter(
				(mr) =>
					showPendingWithProjectRules || !mr.additionalRules?.blocking?.length,
			)
			.filter((mr) => showPendingWithAllApprovals || mr.approvalsLeft > 0);
	};

	const filteredPendingReviewMRs = filterPendingMRs(sortedPendingReviewMRs);
	const filteredMyOpenMRs = sortedMyOpenMRs;
	const filteredRecentlyClosedMRs = sortedRecentlyClosedMRs;

	if (loading) {
		return (
			<div className="space-y-6">
				{[1, 2, 3].map((section) => (
					<div
						key={section}
						className="bg-[var(--color-bg-primary)] rounded shadow p-4"
					>
						<div className="flex items-center gap-2 mb-4">
							<div className="w-8 h-8 rounded bg-[var(--color-bg-secondary)] animate-pulse" />
							<div className="w-48 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
						</div>
						{[1, 2].map((item) => (
							<div
								key={item}
								className="mb-4 py-4 border-b last:border-0 border-[var(--color-border)]"
							>
								<div className="flex justify-between items-start mb-2">
									<div className="w-3/4 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
									<div className="w-16 h-5 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
								</div>
								<div className="flex gap-2 mt-2">
									<div className="w-20 h-4 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
									<div className="w-24 h-4 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
								</div>
							</div>
						))}
					</div>
				))}
			</div>
		);
	}

	return (
		<>
			<RecentPushes pushes={pushesWithoutMR} />

			{error && (
				<div className="bg-[var(--color-error-bg)] border border-[var(--color-error-border)] text-[var(--color-error-text)] px-4 py-3 rounded-lg mb-6 shadow-sm">
					<p className="font-medium">Error loading data</p>
					<p className="text-sm">{error}</p>
				</div>
			)}

			<div className="space-y-6">
				<CollapsibleSection
					storageKey="dashboard-pending-review"
					title={
						<>
							Merge requests to review
							<CountChip count={filteredPendingReviewMRs.length} />
						</>
					}
					icon={AlertOctagon}
					iconColor="text-orange-500 monokai:text-monokai-orange dracula:text-dracula-orange nord:text-nord-orange"
				>
					{filteredPendingReviewMRs.length > 0 ? (
						<>
							{filteredPendingReviewMRs
								.slice(
									(pendingReviewPage - 1) * ITEMS_PER_PAGE,
									pendingReviewPage * ITEMS_PER_PAGE,
								)
								.map((mr) => (
									<MergeRequest
										key={mr.iid}
										mr={mr}
										greyOutBlockingComments={greyOutBlockingComments}
										enableCommentsExpansion={enableCommentsExpansion}
									/>
								))}
							<PaginationButtons
								total={filteredPendingReviewMRs.length}
								currentPage={pendingReviewPage}
								onPageChange={setPendingReviewPage}
								itemsPerPage={ITEMS_PER_PAGE}
							/>
						</>
					) : (
						<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
							No merge requests pending your review
						</div>
					)}
				</CollapsibleSection>

				<CollapsibleSection
					storageKey="dashboard-my-open"
					title={
						<>
							Your merge requests
							<CountChip count={filteredMyOpenMRs.length} />
						</>
					}
					icon={GitPullRequest}
					iconColor="text-blue-500 monokai:text-monokai-blue dracula:text-dracula-cyan nord:text-nord-brightBlue"
				>
					{filteredMyOpenMRs.length > 0 ? (
						<>
							{filteredMyOpenMRs
								.slice(
									(myOpenPage - 1) * ITEMS_PER_PAGE,
									myOpenPage * ITEMS_PER_PAGE,
								)
								.map((mr) => (
									<MergeRequest
										key={mr.iid}
										mr={mr}
										greyOutBlockingComments={greyOutBlockingComments}
										isMyOpenMR={true}
										enableCommentsExpansion={enableCommentsExpansion}
									/>
								))}
							<PaginationButtons
								total={filteredMyOpenMRs.length}
								currentPage={myOpenPage}
								onPageChange={setMyOpenPage}
								itemsPerPage={ITEMS_PER_PAGE}
							/>
						</>
					) : (
						<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
							You haven't created any open merge requests
						</div>
					)}
				</CollapsibleSection>

				<CollapsibleSection
					storageKey="dashboard-recently-closed"
					title={
						<>
							Recently Closed MRs
							<CountChip
								count={loadingClosed ? "..." : filteredRecentlyClosedMRs.length}
							/>
						</>
					}
					icon={GitPullRequestClosed}
					iconColor="text-[var(--color-text-tertiary)]"
				>
					{loadingClosed ? (
						<div className="h-48 bg-[var(--color-bg-secondary)] rounded animate-pulse" />
					) : filteredRecentlyClosedMRs.length > 0 ? (
						<>
							{filteredRecentlyClosedMRs
								.slice(
									(recentlyClosedPage - 1) * ITEMS_PER_PAGE,
									recentlyClosedPage * ITEMS_PER_PAGE,
								)
								.map((mr) => (
									<MergeRequest
										key={mr.iid}
										mr={mr}
										enableCommentsExpansion={enableCommentsExpansion}
									/>
								))}
							<PaginationButtons
								total={filteredRecentlyClosedMRs.length}
								currentPage={recentlyClosedPage}
								onPageChange={setRecentlyClosedPage}
								itemsPerPage={ITEMS_PER_PAGE}
							/>
						</>
					) : (
						<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
							No recently closed merge requests
						</div>
					)}
				</CollapsibleSection>
			</div>
		</>
	);
};
